import { apiErrorSchema } from '@painpal/contracts';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

export interface Parser<T> {
  parse(input: unknown): T;
}

export interface ApiInit {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  json?: unknown;
}

export function apiUrl(path: string): string {
  return new URL(path, window.location.origin).toString();
}

export function errorMessage(error: unknown): string {
  return error instanceof Error && error.name !== 'ZodError' ? error.message : 'Something went wrong.';
}

async function toApiError(response: Response): Promise<ApiRequestError> {
  const body: unknown = await response.json().catch(() => null);
  const parsed = apiErrorSchema.safeParse(body);
  if (parsed.success) {
    return new ApiRequestError(response.status, parsed.data.error.code, parsed.data.error.message);
  }
  return new ApiRequestError(response.status, 'http_error', `Request failed with status ${response.status}`);
}

async function send(path: string, init: ApiInit): Promise<Response> {
  const hasBody = init.json !== undefined;
  const response = await fetch(apiUrl(path), {
    method: init.method ?? 'GET',
    credentials: 'include',
    headers: hasBody ? { 'Content-Type': 'application/json' } : undefined,
    body: hasBody ? JSON.stringify(init.json) : undefined,
  });
  if (!response.ok) throw await toApiError(response);
  return response;
}

export async function apiJson<T>(path: string, parser: Parser<T>, init: ApiInit = {}): Promise<T> {
  const response = await send(path, init);
  const body: unknown = await response.json();
  return parser.parse(body);
}

export async function apiVoid(path: string, init: ApiInit): Promise<void> {
  await send(path, init);
}
